// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file
 * @author James Reid
 */

// @ts-check

// @@imports-package
import { ListSelect } from "../../../components/elements/ListSelect.js"

// @@imports-submodule
import { getMembraneBreakdown } from "./breakdowns/index.js"

// @@imports-utils
import { cloneTemplate, getDeckImageUrl } from "../../../utils/index.js"

// @@imports-types
import { State } from "../../../types/index.js"
import { Bom, EpdmCalculator, MEMBRANES, MEMBRANE_TYPES, Membrane, SEAM_DIRECTIONS } from "../../../../../package/index.js"
import { App } from "../../../app/App.js"

// @@body
/**
 * 
 * @param {HTMLElement} card
 * @param {Bom} bom
 * @param {State} state
 */
const populateMembraneCard = (card, bom, state) => {
    const footer = cloneTemplate("product-options-membrane")
    card.append(footer)

    const thickness = card.querySelector("span.thickness")
    if (thickness) {
        const product = bom.lineItems[0].product
        const thicknessM = product.thickness * 1000
        thickness.innerText = product.type === MEMBRANE_TYPES.onePiece
            ? thicknessM.toFixed(2)
            : (thicknessM + 1.4).toFixed(2)
    }

    const selector = /** @type {ListSelect} */ (card.querySelector("list-select[data-id='membrane-option']"))
    selector?.addEventListener("change", () => {
        const membrane = Object.values(MEMBRANES).find(membrane => {
            return membrane.id === selector.value
        })
        const config = state.quote.calculator?.config

        if (membrane && config && config.membrane !== membrane) {
            config.membrane = membrane
            config.seamDirection = SEAM_DIRECTIONS.any
            card.dispatchEvent(new Event("change"))
            App.dispatchStateUpdated()
        }
    })
    ListSelect.preConnectValue(
        selector,
        state.quote.calculator?.config.membrane.id
    )

    const seamDirectionSelector = /** @type {ListSelect} */ (card
        .querySelector("list-select[data-id='seam-direction']"))
    seamDirectionSelector.addEventListener("change", () => {
        const config = state.quote.calculator?.config
        const seamDirection = seamDirectionSelector.value

        if (config && config.seamDirection !== seamDirection) {
            config.seamDirection = seamDirection
            card.dispatchEvent(new Event("change"))
            App.dispatchStateUpdated()
        }
    })
    ListSelect.preConnectValue(
        seamDirectionSelector,
        state.quote.calculator?.config.seamDirection
    )

    const epdmCalculator = /** @type {EpdmCalculator} */ (state.quote.calculator)
    const { bom: { price }, config: { membrane, seamDirection } } = epdmCalculator
    card.querySelectorAll("list-select[data-id='membrane-option'] div.dropdown li")
        .forEach(option => {
            const upgrade = /** @type {HTMLSpanElement?} */
                (option.querySelector("span.upgrade"))
            const upgradeMembrane = Object.values(MEMBRANES).find(membrane => {
                return membrane.id === /** @type {HTMLLIElement} */
                    (option).dataset.value
            })
            if (!upgrade || !upgradeMembrane) { return }

            try {
                epdmCalculator.config.membrane = upgradeMembrane
                epdmCalculator.config.seamDirection = SEAM_DIRECTIONS.any
                epdmCalculator.updateBom()
                const { bom: { price: upgradePrice } } = epdmCalculator

                const priceDelta = upgradePrice - price
                if (upgradeMembrane === membrane) {
                    upgrade.innerText = "Selected"
                    upgrade.parentElement?.classList.remove("--price-detail")
                }
                else {
                    upgrade.innerText = priceDelta === 0 ? "Same Price"
                            : priceDelta < 0 ? `- £${Math.abs(priceDelta).toFixed(2)}`
                            : `+ £${Math.abs(priceDelta).toFixed(2)}`
                    upgrade.parentElement?.classList.add("--price-detail")
                }
            }
            catch {
                upgrade.innerText = "Option not available"
                option.classList.add("inactive")
            }
        })
    epdmCalculator.config.membrane = membrane
    epdmCalculator.config.seamDirection = seamDirection
    epdmCalculator.updateBom()

    card.querySelectorAll("list-select[data-id='seam-direction'] div.dropdown li")
        .forEach(option => {
            const upgrade = /** @type {HTMLSpanElement?} */
                (option.querySelector("span.upgrade"))
            const optionSeamDirection = /** @type {HTMLLIElement} */
                    (option).dataset.value
            if (!upgrade || !optionSeamDirection) { return }

            try {
                epdmCalculator.config.seamDirection = optionSeamDirection
                epdmCalculator.updateBom()
                const { bom: { price: upgradePrice } } = epdmCalculator

                const priceDelta = upgradePrice - price
                if (optionSeamDirection === seamDirection) {
                    upgrade.innerText = "Selected"
                    upgrade.parentElement?.classList.remove("--price-detail")
                }
                else {
                    upgrade.innerText = priceDelta === 0 ? "Same Price"
                        : priceDelta < 0 ? `- £${Math.abs(priceDelta).toFixed(2)}`
                        : `+ £${Math.abs(priceDelta).toFixed(2)}`
                    upgrade.parentElement?.classList.add("--price-detail")
                }
            }
            catch {
                upgrade.innerText = "Option not available"
                option.classList.add("inactive")
            }
        })
    epdmCalculator.config.seamDirection = seamDirection
    epdmCalculator.updateBom()

    const membraneCount = bom
        .lineItems
        .filter(lineItem => lineItem.product instanceof Membrane)
        .length
    if (membraneCount <= 1) { seamDirectionSelector.classList.add("closed") }

    const subtotal = /** @type {HTMLSpanElement?} */
        (card.querySelector("span.price"))
    if (subtotal) { subtotal.innerText = bom.price.toFixed(2) }

    card.querySelector("tbody")?.replaceWith(getMembraneBreakdown(bom))

    const membraneChoice = card.querySelector("span.membrane-choice")
    if (membraneChoice) {
        membraneChoice.innerText = bom.lineItems[0].product.name
    }

    const image = document.createElement("img")
    getMembraneBreakdownImageUrl(bom, state)
        .then(url => image.src = url)
        .catch(error => console.error(error))
    card.querySelector("div")?.append(image)
}

/**
 * 
 * @param {Bom} bom 
 * @param {State} state 
 * @returns 
 */
const getMembraneBreakdownImageUrl = async (bom, state) => {
    const zones = new Map()
    for (const { detail } of bom.lineItems) {
        const zone = zones.get(detail.zone)
        if (zone) {
            const { fraction, id } = detail
            zone.fractions.push({ fraction, id })
        }
        else {
            const { fraction, flipped, id } = detail
            const fractions = [{ fraction, id }]
            zones.set(detail.zone, { fractions, flipped })
        }
    }

    const membraneBreakdownImageUrl = await getDeckImageUrl(state.deck.client, {
        rotation: 0,
        skew: 0,
        shadowWidth: 0,
        forceRender: true,
        zones
    })

    return membraneBreakdownImageUrl
}

// @@exports
export { populateMembraneCard }
